package fr.frux.feature.harvestcreate

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.shared.content.DescriptionLength
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestTarget
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/** Créneaux horaires proposés (#167) : matinée, fin de matinée, après-midi, fin de journée. */
internal val HOUR_OPTIONS = listOf(9, 11, 14, 17)

/** Nombre de pastilles jour : aujourd'hui + 3 jours suivants (spec #167). */
private const val DAY_OPTIONS_COUNT = 4

/**
 * Horizon de planification client (#339) : le serveur accepte jusqu'à un an, mais la règle
 * produit borne le choix à 4 semaines pour rester sur des créneaux réalistes.
 */
internal const val MAX_DAYS_AHEAD = 28

/**
 * ViewModel du formulaire « Lancer une récolte » (#167), poussé depuis la fiche d'arbre, la carte
 * ou la fiche d'un lieu (ADR 0035). La cible (arbre ou lieu) est un simple rappel, non modifiable :
 * elle n'est chargée que pour l'affichage, en échec silencieux comme HarvestDetailViewModel.
 * Sur un lieu dont on est propriétaire, « Chez moi, je vous invite » démarre coché.
 */
@OptIn(ExperimentalTime::class)
class CreateHarvestViewModel(
    val target: HarvestTarget,
    private val harvestRepository: HarvestRepository,
    private val treeRepository: TreeRepository,
    private val siteRepository: SiteRepository,
    /** Injectable pour tester les créneaux passés (#310) sans dépendre de l'heure du poste. */
    private val clock: Clock = Clock.System,
) : ViewModel() {
    companion object {
        private const val TAG = "CreateHarvestViewModel"
    }

    private val timeZone = TimeZone.currentSystemDefault()

    /**
     * Figé à la construction : les pastilles jour ne doivent pas glisser si l'écran reste ouvert
     * à cheval sur minuit — l'utilisateur choisit parmi ce qu'il a vu.
     */
    val today: LocalDate =
        clock
            .now()
            .toLocalDateTime(timeZone)
            .date

    /** Borne haute du date picker « Autre date » (#339). */
    val maxDate: LocalDate = today.plus(MAX_DAYS_AHEAD, DateTimeUnit.DAY)

    val dayOptions: List<LocalDate> = (0 until DAY_OPTIONS_COUNT).map { offset -> today.plus(offset, DateTimeUnit.DAY) }

    private val _state =
        MutableStateFlow(
            CreateHarvestState(
                target = target,
                selectedDate = dayOptions[1], // défaut : demain
                selectedHour = HOUR_OPTIONS.first(), // défaut : 9h
            ),
        )
    val state: StateFlow<CreateHarvestState> = _state.asStateFlow()

    private val _events = Channel<CreateHarvestEvent>(Channel.BUFFERED)
    val events: Flow<CreateHarvestEvent> = _events.receiveAsFlow()

    init {
        when (target) {
            is HarvestTarget.Tree -> loadTree(target.id)
            is HarvestTarget.Site -> loadSite(target.id)
        }
    }

    private fun loadTree(treeId: Long) {
        viewModelScope.launch {
            _state.update { it.copy(isTargetLoading = true) }
            treeRepository
                .getTreeById(treeId)
                .collect { result: Result<Tree> ->
                    result.fold(
                        onSuccess = { tree -> _state.update { it.copy(tree = tree, isTargetLoading = false) } },
                        onFailure = { error ->
                            Logger.d(TAG, "⚠️ Arbre indisponible pour la création de récolte : ${error.message}")
                            _state.update { it.copy(isTargetLoading = false) }
                        },
                    )
                }
        }
    }

    private fun loadSite(siteId: Long) {
        viewModelScope.launch {
            _state.update { it.copy(isTargetLoading = true) }
            siteRepository
                .getSite(siteId)
                .collect { result: Result<Site> ->
                    result.fold(
                        onSuccess = { site -> _state.update { it.withSite(site) } },
                        onFailure = { error ->
                            Logger.d(TAG, "⚠️ Lieu indisponible pour la création de récolte : ${error.message}")
                            _state.update { it.copy(isTargetLoading = false) }
                        },
                    )
                }
        }
    }

    /**
     * Un créneau (jour + heure de [HOUR_OPTIONS]) déjà passé n'est ni sélectionnable ni
     * soumissible (#310) : le serveur le refuserait en 400 INVALID_DATE. Évalué à l'heure
     * courante, pas à [today] figé — l'écran peut rester ouvert.
     */
    fun isPastSlot(
        date: LocalDate,
        hour: Int,
    ): Boolean =
        LocalDateTime(date.year, date.monthNumber, date.day, hour, 0)
            .toInstant(timeZone) <= clock.now()

    /** Vrai quand plus aucun créneau du jour n'est à venir : la pastille jour se désactive (#310). */
    fun isDayFullyPast(date: LocalDate): Boolean = HOUR_OPTIONS.all { isPastSlot(date, it) }

    /** Fenêtre de planification autorisée (#339) : aujourd'hui à J+28, jour déjà passé exclu. */
    fun isDateSelectable(date: LocalDate): Boolean = date in today..maxDate && !isDayFullyPast(date)

    /**
     * Sélectionner un jour dont l'heure courante est passée bascule sur le premier créneau à venir
     * (#310). Une date hors fenêtre autorisée (#339) est ignorée silencieusement : ni le date
     * picker (bornes désactivées) ni les pastilles ne peuvent en produire une en usage normal.
     */
    fun onDaySelected(date: LocalDate) {
        if (!isDateSelectable(date)) return
        _state.update {
            val hour =
                if (isPastSlot(date, it.selectedHour)) {
                    HOUR_OPTIONS.firstOrNull { h -> !isPastSlot(date, h) } ?: it.selectedHour
                } else {
                    it.selectedHour
                }
            it.copy(selectedDate = date, selectedHour = hour)
        }
    }

    fun onHourSelected(hour: Int) = _state.update { it.copy(selectedHour = hour) }

    // Tronqué à la borne serveur (#294, #273) : garantie d'état même si un chemin de saisie
    // contourne la coupe du champ (collage programmatique, restauration).
    fun onDescriptionChange(text: String) = _state.update { it.copy(description = text.take(DescriptionLength.MAX)) }

    /** Sans limite → 8 (défaut du prototype) ; sinon +1, plafonné à 30. */
    fun onMaxParticipantsIncrement() =
        _state.update {
            it.copy(maxParticipants = (it.maxParticipants?.plus(1) ?: 8).coerceAtMost(30))
        }

    /** À 2 ou moins → repasse à « sans limite » ; sinon -1. */
    fun onMaxParticipantsDecrement() =
        _state.update {
            val current = it.maxParticipants
            it.copy(maxParticipants = if (current == null || current <= 2) null else current - 1)
        }

    /** Sans effet pour qui n'est pas propriétaire du lieu : le serveur le refuserait en 403. */
    fun onHostedByOwnerChanged(hosted: Boolean) = _state.update { it.copy(hostedByOwner = hosted && it.canHostAsOwner) }

    /**
     * Ajoute la consigne à la description, une seule fois par chip. La phrase vient de l'écran, qui
     * la résout par `stringResource` (asynchrone sur js, donc jamais depuis le ViewModel).
     */
    fun onAccessHintClicked(
        hint: AccessHint,
        sentence: String,
    ) {
        if (sentence.isBlank()) return
        _state.update {
            if (hint in it.appliedHints) {
                it
            } else {
                it.copy(
                    appliedHints = it.appliedHints + hint,
                    description = it.description.withSentence(sentence).take(DescriptionLength.MAX),
                )
            }
        }
    }

    fun consumeActionError() = _state.update { it.copy(actionError = null) }

    fun submit() {
        val current = _state.value
        if (current.isSubmitting) return
        // Garde locale (#310) : sans elle, le serveur répond 400 INVALID_DATE et l'utilisateur
        // reçoit un message générique après un aller-retour réseau.
        if (isPastSlot(current.selectedDate, current.selectedHour)) {
            _state.update { it.copy(actionError = CreateHarvestError.PastSlot) }
            return
        }
        // Synchrone, avant tout `launch` : garde anti double-tap.
        _state.update { it.copy(isSubmitting = true, actionError = null) }

        val scheduledDate = current.scheduledDateMillis(timeZone)
        viewModelScope.launch {
            harvestRepository
                .createHarvest(
                    target = target,
                    scheduledDate = scheduledDate,
                    description = current.description.trim().ifEmpty { null },
                    maxParticipants = current.maxParticipants,
                    hostedByOwner = current.hostedByOwner,
                ).fold(
                    onSuccess = { harvest ->
                        Logger.i(TAG, "✅ Récolte créée sur $target")
                        _state.update { it.copy(isSubmitting = false) }
                        _events.send(CreateHarvestEvent.Created(harvest))
                    },
                    onFailure = { error ->
                        // Échec attendu (400 métier, réseau…) : affiché à l'écran, jamais en
                        // Logger.e — le pont vers Sentry (#175) en ferait un event (#310).
                        if (error.isExpectedFailure) {
                            Logger.i(TAG, "⚠️ Création de récolte refusée : ${error.message}")
                        } else {
                            Logger.e(TAG, "❌ Création de récolte échouée", error)
                        }
                        _state.update {
                            it.copy(isSubmitting = false, actionError = CreateHarvestError.Failure(error.asAppError()))
                        }
                    },
                )
        }
    }
}

private fun String.withSentence(sentence: String): String {
    val base = trim().trimEnd('.', ' ')
    return if (base.isEmpty()) sentence else "$base. $sentence"
}

/** Événements one-shot vers l'UI. Ne jamais les stocker en état : ils seraient rejoués. */
sealed interface CreateHarvestEvent {
    /** Publication réussie : l'hôte navigue vers la fiche récolte, en remplaçant le formulaire. */
    data class Created(
        val harvest: Harvest,
    ) : CreateHarvestEvent
}

data class CreateHarvestState(
    val target: HarvestTarget,
    val tree: Tree? = null,
    val site: Site? = null,
    val isTargetLoading: Boolean = false,
    val selectedDate: LocalDate,
    val selectedHour: Int,
    val description: String = "",
    val maxParticipants: Int? = null,
    /** « Chez moi, je vous invite » (ADR 0035), coché d'office pour le propriétaire du lieu. */
    val hostedByOwner: Boolean = false,
    val canHostAsOwner: Boolean = false,
    /** Consignes déjà ajoutées à la description : une chip ne s'ajoute qu'une fois. */
    val appliedHints: Set<AccessHint> = emptySet(),
    val isSubmitting: Boolean = false,
    /** Refus de la soumission (#176), typé : le libellé est résolu par l'écran. */
    val actionError: CreateHarvestError? = null,
) {
    /**
     * Le défaut « chez moi » se pose la première fois que la propriété est connue : la copie du
     * cache n'a pas `isOwner`, la réponse réseau suivante le révèle. Ensuite, un rafraîchissement
     * ne doit pas défaire le choix de l'utilisateur.
     */
    internal fun withSite(loaded: Site): CreateHarvestState =
        copy(
            site = loaded,
            isTargetLoading = false,
            canHostAsOwner = loaded.isOwner,
            hostedByOwner = if (!canHostAsOwner && loaded.isOwner) true else hostedByOwner && loaded.isOwner,
        )

    /** `scheduledDate` du contrat = epoch millis (#79), combinaison du jour et de l'heure choisis. */
    @OptIn(ExperimentalTime::class)
    fun scheduledDateMillis(timeZone: TimeZone): Long =
        LocalDateTime(selectedDate.year, selectedDate.monthNumber, selectedDate.day, selectedHour, 0)
            .toInstant(timeZone)
            .toEpochMilliseconds()
}

/**
 * Pourquoi la publication a été refusée (#176). Deux origines à distinguer : la garde locale de
 * créneau passé (#310), qui n'est jamais partie au serveur, et tout échec d'appel.
 */
sealed interface CreateHarvestError {
    /** Garde de soumission (#310) : le serveur refuserait en 400 INVALID_DATE. */
    data object PastSlot : CreateHarvestError

    data class Failure(
        val error: AppError,
    ) : CreateHarvestError
}
